import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    Box,
    Button,
    Checkbox,
    CircularProgress,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControlLabel,
    FormGroup,
    InputAdornment,
    Radio,
    TextField,
    Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import HelpIcon from "@mui/icons-material/Help";
import { useQuery } from "react-query";
import axiosClient from "../../lib/axios";
import useDebounce from "../../lib/useDebounce";
import BtnCarregarDados from "../../components/BtnCarregarDados";
import useMobileScreen from "../../lib/useMobileScreen";
import DialogAppBar from "../../components/DialogAppBar";
import { IDataNotaFiscalEntrada } from "../../types/api/fiscal/pesquisar-nota-fiscal-entrada";
import { formatarValorMonetario } from "../../helpers/masks";

interface DialogContentSelecionarNotaFiscalProps {
    defaultValue: IDataNotaFiscalEntrada | IDataNotaFiscalEntrada[];
    extraParams?: { [key: string]: any };
    handleClose: () => void;
    onChange: (arg: IDataNotaFiscalEntrada | IDataNotaFiscalEntrada[]) => void;
    multiple?: boolean;
}

export default function DialogContentSelecionarNotaFiscal({
    defaultValue,
    extraParams = {},
    onChange,
    handleClose,
    multiple = false,
}: DialogContentSelecionarNotaFiscalProps) {

    const telaMobile = useMobileScreen();
    const [limit, setLimit] = useState(20);
    const [palavraChave, setPalavraChave] = useState("");
    const [selecionados, setSelecionados] = useState<IDataNotaFiscalEntrada[]>(
        multiple
            ? [...(defaultValue as IDataNotaFiscalEntrada[])]
            : [defaultValue as IDataNotaFiscalEntrada]
    );

    const inputRef = useRef<HTMLInputElement>(null);


    const debouncedKeyword = useDebounce(palavraChave);

    const { data, isFetching, isLoading } = useQuery({
        queryKey: ["pesquisar-notas-fiscais", debouncedKeyword, limit],
        queryFn: () =>
            axiosClient.get("/fiscal/pesquisar-notas-fiscais-entrada", {
                params: {
                    limit,
                    page: 1,
                    ...extraParams,
                    pesquisar: debouncedKeyword,
                },
            }),
        cacheTime: 0,
        staleTime: 0,
        onSuccess: () => {
            if (inputRef.current) {
                inputRef.current.focus();
            }
        },
    });

    const { requestNota, dataNota } = useMemo(
        () => ({
            requestNota: data?.data,
            dataNota: data?.data.data,
        }),
        [data]
    );

    // Funções Manipuladoras
    const handleClick = useCallback(
        (item: IDataNotaFiscalEntrada) => {
            if (multiple) {
                const index = selecionados.findIndex(
                    (i) => i.numero_nota === item.numero_nota
                );
                if (index === -1) {
                    setSelecionados((prevState) => [...prevState, item]);
                } else {
                    setSelecionados((prevState) =>
                        prevState.filter((_, i) => i !== index)
                    );
                }
            }

            if (!multiple) setSelecionados([item]);
        },
        [selecionados, multiple]
    );

    const handleSubmit = useCallback(() => {
        if (!multiple) {
            onChange(selecionados[0]);
            return;
        }

        onChange(selecionados);
    }, [onChange, selecionados, multiple]);

    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.focus();
        }
    }, []);

    return (
        <>
            {telaMobile && (
                <DialogAppBar {...{ handleClose }} title="Selecionar nota fiscal" />
            )}

            <DialogTitle>Selecionar nota fiscal</DialogTitle>

            <DialogContent sx={{ mt: { xs: 2, md: 0 } }}>
                <Box display="flex" alignItems="center" gap={2} py={2}>
                    <TextField
                        value={palavraChave}
                        fullWidth
                        inputRef={inputRef}
                        label="Pesquisar"
                        slotProps={{
                            htmlInput: { sx: { textTransform: "uppercase", tabIndex: 0 } },
                            input: {
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <SearchIcon />
                                    </InputAdornment>
                                )
                            }
                        }}
                    onChange={(e) => setPalavraChave(e.target.value)}
                    />
                </Box>

                <Divider sx={{ mb: 2 }} />

                {isLoading && (
                    <Box display="flex" justifyContent="center" my={2}>
                        <CircularProgress />
                    </Box>
                )}

                {!isLoading && (
                    <FormGroup>
                        <Box
                            display="flex"
                            overflow="auto"
                            height={{ xs: 550, md: 350 }}
                            flexDirection="column"
                        >
                            {dataNota && dataNota.length <= 0 && (
                                <Box
                                    display="flex"
                                    justifyContent="center"
                                    paddingY={2}
                                    gap={1}
                                    color="gray"
                                >
                                    <HelpIcon />
                                    <Typography>Nenhum resultado encontrado</Typography>
                                </Box>
                            )}

                            {dataNota &&
                                dataNota.length > 0 &&
                                dataNota.map((i: IDataNotaFiscalEntrada, index: number) => {
                                    const isChecked = selecionados.some(
                                        (item) =>
                                            `${item.numero_nota}` === `${i.numero_nota}`
                                    );
                                    return (
                                        <FormControlLabel
                                            key={index}
                                            onChange={() => handleClick(i)}
                                            onKeyDown={(e) => {
                                                if (e.key === "Enter") {
                                                    handleClick(i);
                                                }
                                            }}
                                            control={
                                                multiple ? (
                                                    <Checkbox checked={isChecked} />
                                                ) : (
                                                    <Radio checked={isChecked} />
                                                )
                                            }
                                            sx={{ mb: 2, alignItems: 'flex-start' }}
                                            label={
                                                <Box sx={{ width: '100%', ml: 1 }}>
                                                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                                        <Typography fontWeight={600} variant="body1">
                                                            {`DANFE Nº ${i.numero_nota || 'N/A'}/${i.serie_nota || 'N/A'}`}
                                                        </Typography>
                                                        <Typography fontWeight={'bold'} variant="body1">
                                                            {`${formatarValorMonetario(i.valor_total)}`}
                                                        </Typography>
                                                    </Box>

                                                    <Typography variant="body2" color="text.secondary" mb={0.5}>
                                                        Chave: {i.chave || 'N/A'}
                                                    </Typography>

                                                    <Typography variant="body2" color="text.secondary" mb={0.5}>
                                                        <Box component="span" fontWeight={500}>Emitente</Box>
                                                    </Typography>

                                                    <Typography variant="body2" mb={0.5}>
                                                        {`${i.tipo_emissor || ''}${i.codigo_emissor || ""} ${i.nome_emissor || 'N/A'}`}
                                                    </Typography>

                                                    <Typography variant="body2" color="text.secondary" mb={0.5}>
                                                        <Box component="span" fontWeight={500}>Destinatário</Box>
                                                    </Typography>

                                                    <Typography variant="body2">
                                                        {`${i.tipo_destinatario || ''}${i.codigo_destinatario || ''} ${i.nome_destinatario || 'N/A'}`}
                                                    </Typography>
                                                </Box>
                                            }
                                        />
                                    );
                                })}
                            <BtnCarregarDados
                                {...{ limit, isFetching }}
                                total={requestNota.total}
                                action={() => setLimit((prevState) => prevState + 20)}
                            />
                        </Box>
                    </FormGroup>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>Cancelar</Button>
                <Button
                    autoFocus
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={selecionados?.[0].numero_nota === ""}
                >
                    Avançar
                </Button>
            </DialogActions>
        </>
    );
}


